import { useState, useCallback } from "react";
import html2canvas from "html2canvas";

interface CaptureResult {
  blob: Blob;
  canvas: HTMLCanvasElement;
}

interface UseCaptureReturn {
  capture: () => Promise<CaptureResult>;
  capturing: boolean;
}

const OVERLAY_SELECTORS = [
  ".DashboardSetting",
  ".photo",
  ".credit",
  ".dashboard__settings",
] as const;

export function useCapture(): UseCaptureReturn {
  const [capturing, setCapturing] = useState(false);

  const capture = useCallback(async (): Promise<CaptureResult> => {
    setCapturing(true);

    const element = document.querySelector(".Dashboard") as HTMLElement | null;
    if (!element) {
      setCapturing(false);
      throw new Error("Dashboard element not found");
    }

    const overlays = OVERLAY_SELECTORS.map(
      (sel) => document.querySelector(sel) as HTMLElement | null
    );

    // Wait for all images to fully render
    const images = element.querySelectorAll("img");
    await Promise.all(
      Array.from(images).map(
        (img) =>
          new Promise<void>((resolve) => {
            if (img.complete) resolve();
            else {
              img.onload = () => resolve();
              img.onerror = () => resolve();
            }
          })
      )
    );

    try {
      // Hide overlays
      overlays.forEach((el) => {
        if (el) el.style.display = "none";
      });

      const rect = element.getBoundingClientRect();
      const maxDim = 1920;
      const scale = Math.min(
        window.devicePixelRatio * 2,
        maxDim / Math.max(rect.width, rect.height)
      );

      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
        allowTaint: true,
        width: rect.width,
        height: rect.height,
        windowWidth: rect.width,
        windowHeight: rect.height,
      });

      const blob: Blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("canvas toBlob failed"))),
          "image/png"
        );
      });

      return { blob, canvas };
    } finally {
      setCapturing(false);
      // Restore overlays
      overlays.forEach((el) => {
        if (el) el.style.display = "";
      });
    }
  }, []);

  return { capture, capturing };
}
